import { Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { NodeSelection } from '@tiptap/pm/state';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HighlightIcon from '@mui/icons-material/Highlight';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TitleIcon from '@mui/icons-material/Title';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
	Box,
	Button,
	Chip,
	Divider,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Popover,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';

import { ExpressionModel } from '../../Models/ExpressionModel';
import {
	PLACEHOLDER_MATCH_REGEX,
	StringTemplateFormat,
	TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE,
	TEMPLATE_CONTENT_PLACEHOLDER_TOKEN,
	getPlaceholderIdAtPosition,
	extractPlaceholderIds,
	isSystemTemplatePlaceholder,
} from '../../Models/StringTemplateModel';
import { VariableScope } from '../../Models/VariableScopes/VariableScope';
import TemplatePlaceholderMenu, { TEMPLATE_PLACEHOLDER_MENU_LABEL } from '../../../Content/TemplatePlaceholderMenu';
import {
	contentToHtml,
	contentToRawHtmlPreview,
	formatHtmlSource,
	getRawHtmlEditorContent,
	htmlToContent,
	serializeHtmlDocument,
	shouldPreserveRawHtml,
	updateRawHtmlBodyContent,
} from './templateHtmlSerialization';

// ---------------------------------------------------------------------------
// Custom Tiptap node for expression placeholders
// ---------------------------------------------------------------------------

type PlaceholderNodeViewProps = {
	node: { attrs: { expressionId: string; configured: boolean } };
	selected: boolean;
};

function PlaceholderNodeView({ node, selected }: PlaceholderNodeViewProps) {
	const label = isSystemTemplatePlaceholder(node.attrs.expressionId) ? 'Child content' : node.attrs.expressionId;

	return (
		<NodeViewWrapper as="span" data-expression-id={node.attrs.expressionId} data-drag-handle>
			<Chip
				label={label}
				size="small"
				color="primary"
				variant={selected ? 'filled' : 'outlined'}
				sx={{ cursor: 'pointer', verticalAlign: 'middle', mx: 0.25 }}
			/>
		</NodeViewWrapper>
	);
}

const PlaceholderExtension = TiptapNode.create({
	name: 'expressionPlaceholder',
	group: 'inline',
	inline: true,
	atom: true,

	addAttributes() {
		return {
			expressionId: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-expression-id'),
				renderHTML: (attributes) => ({ 'data-expression-id': attributes.expressionId }),
			},
			configured: {
				default: false,
				parseHTML: (element) => element.getAttribute('data-configured') === 'true',
				renderHTML: (attributes) => ({ 'data-configured': attributes.configured }),
			},
		};
	},

	parseHTML() {
		return [{ tag: 'span[data-expression-id]' }];
	},

	renderHTML({ node, HTMLAttributes }) {
		return ['span', mergeAttributes(HTMLAttributes, { class: 'expression-placeholder' }), `{{${node.attrs.expressionId}}}`];
	},

	addNodeView() {
		return ReactNodeViewRenderer(PlaceholderNodeView as any);
	},
});

function getParsableClipboardHtml(clipboardData: DataTransfer | null): string | null {
	const text = clipboardData?.getData('text/plain');
	const trimmedText = text?.trim();
	if (trimmedText) {
		const doc = new DOMParser().parseFromString(trimmedText, 'text/html');
		const hasHtmlSourceTags = /<([a-z][\w:-]*)(\s|>|\/)/i.test(trimmedText);
		if (hasHtmlSourceTags && (doc.body.children.length > 0 || doc.head.children.length > 0 || shouldPreserveRawHtml(trimmedText))) {
			return trimmedText;
		}
	}

	const html = clipboardData?.getData('text/html');
	if (html?.trim()) {
		return html;
	}

	return null;
}

function copyTextBlockStyles(source: Element, target: HTMLElement) {
	const computed = source.ownerDocument.defaultView?.getComputedStyle(source);
	if (!computed) {
		return;
	}

	['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'color', 'lineHeight', 'textAlign', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft'].forEach(
		(property) => {
			target.style[property as any] = computed[property as any];
		},
	);
}

function placeCaretInside(element: HTMLElement) {
	const doc = element.ownerDocument;
	const range = doc.createRange();
	const selection = doc.getSelection();
	range.selectNodeContents(element);
	range.collapse(true);
	selection?.removeAllRanges();
	selection?.addRange(range);
}

function getSelectionElement(doc: Document): Element | null {
	const node = doc.getSelection()?.anchorNode;
	if (!node) {
		return null;
	}

	return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
}

function insertStyledParagraphAfterSelection(doc: Document): boolean {
	const selectedElement = getSelectionElement(doc);
	if (!selectedElement || selectedElement.closest('li')) {
		return false;
	}

	const sourceBlock = selectedElement.closest('p,div,h1,h2,h3,h4,h5,h6') ?? selectedElement.closest('td,th');
	if (!sourceBlock) {
		return false;
	}

	const paragraph = doc.createElement('p');
	copyTextBlockStyles(sourceBlock, paragraph);
	paragraph.appendChild(doc.createElement('br'));

	if (sourceBlock.matches('td,th')) {
		sourceBlock.appendChild(paragraph);
	} else {
		sourceBlock.after(paragraph);
	}

	placeCaretInside(paragraph);
	return true;
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function EditorToolbar({ editor }: { editor: Editor }) {
	const [headingAnchor, setHeadingAnchor] = useState<null | HTMLElement>(null);
	const [linkAnchor, setLinkAnchor] = useState<null | HTMLElement>(null);
	const [linkUrl, setLinkUrl] = useState('');

	const openLinkPopover = (e: React.MouseEvent<HTMLElement>) => {
		setLinkUrl(editor.getAttributes('link').href ?? '');
		setLinkAnchor(e.currentTarget);
	};

	const closeLinkPopover = () => setLinkAnchor(null);

	const applyLink = () => {
		if (linkUrl.trim() === '') {
			editor.chain().focus().unsetLink().run();
		} else {
			editor.chain().focus().setLink({ href: linkUrl.trim(), target: '_blank' }).run();
		}
		closeLinkPopover();
	};

	return (
		<Box sx={{ display: 'flex', gap: 0.5, p: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
			{/* Heading */}
			<Tooltip title="Heading">
				<IconButton size="small" onClick={(e) => setHeadingAnchor(e.currentTarget)} color={editor.isActive('heading') ? 'primary' : 'default'}>
					<TitleIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Menu anchorEl={headingAnchor} open={Boolean(headingAnchor)} onClose={() => setHeadingAnchor(null)}>
				{([1, 2, 3] as const).map((level) => (
					<MenuItem
						key={level}
						selected={editor.isActive('heading', { level })}
						onClick={() => {
							editor.chain().focus().toggleHeading({ level }).run();
							setHeadingAnchor(null);
						}}
					>
						H{level}
					</MenuItem>
				))}
				<MenuItem
					selected={!editor.isActive('heading')}
					onClick={() => {
						editor.chain().focus().setParagraph().run();
						setHeadingAnchor(null);
					}}
				>
					Normal
				</MenuItem>
			</Menu>
			<Divider orientation="vertical" flexItem />
			{/* Inline formatting */}
			<Tooltip title="Bold">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={editor.isActive('bold') ? 'primary' : 'default'}>
					<FormatBoldIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Italic">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={editor.isActive('italic') ? 'primary' : 'default'}>
					<FormatItalicIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Underline">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} color={editor.isActive('underline') ? 'primary' : 'default'}>
					<FormatUnderlinedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Strikethrough">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleStrike().run()} color={editor.isActive('strike') ? 'primary' : 'default'}>
					<FormatStrikethroughIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Highlight">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleHighlight().run()} color={editor.isActive('highlight') ? 'primary' : 'default'}>
					<HighlightIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title={editor.isActive('link') ? 'Edit link' : 'Add link'}>
				<IconButton size="small" onClick={openLinkPopover} color={editor.isActive('link') ? 'primary' : 'default'}>
					<LinkIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Popover
				open={Boolean(linkAnchor)}
				anchorEl={linkAnchor}
				onClose={closeLinkPopover}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Box
					component="form"
					onSubmit={(e) => {
						e.preventDefault();
						applyLink();
					}}
					sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', minWidth: 300 }}
				>
					<TextField
						autoFocus
						size="small"
						label="URL"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						placeholder="https://"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LinkIcon sx={{ fontSize: 16 }} />
								</InputAdornment>
							),
						}}
						sx={{ flex: 1 }}
					/>
					<Button type="submit" variant="contained" size="small">
						Apply
					</Button>
					{editor.isActive('link') && (
						<Tooltip title="Remove link">
							<IconButton
								size="small"
								onClick={() => {
									editor.chain().focus().unsetLink().run();
									closeLinkPopover();
								}}
							>
								<LinkOffIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Popover>
			<Divider orientation="vertical" flexItem />
			{/* Alignment */}
			<Tooltip title="Align left">
				<IconButton
					size="small"
					onClick={() => editor.chain().focus().setTextAlign('left').run()}
					color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
				>
					<FormatAlignLeftIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Align center">
				<IconButton
					size="small"
					onClick={() => editor.chain().focus().setTextAlign('center').run()}
					color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
				>
					<FormatAlignCenterIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Align right">
				<IconButton
					size="small"
					onClick={() => editor.chain().focus().setTextAlign('right').run()}
					color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
				>
					<FormatAlignRightIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Justify">
				<IconButton
					size="small"
					onClick={() => editor.chain().focus().setTextAlign('justify').run()}
					color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
				>
					<FormatAlignJustifyIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			{/* Lists */}
			<Tooltip title="Bullet list">
				<IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={editor.isActive('bulletList') ? 'primary' : 'default'}>
					<FormatListBulletedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Numbered list">
				<IconButton
					size="small"
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					color={editor.isActive('orderedList') ? 'primary' : 'default'}
				>
					<FormatListNumberedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

type RawHtmlEditorToolbarProps = {
	onCommand: (command: string, value?: string) => void;
	onLink: () => void;
};

function RawHtmlEditorToolbar({ onCommand, onLink }: RawHtmlEditorToolbarProps) {
	return (
		<Box onMouseDown={(event) => event.preventDefault()} sx={{ display: 'flex', gap: 0.5, p: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
			<Tooltip title="Bold">
				<IconButton size="small" onClick={() => onCommand('bold')}>
					<FormatBoldIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Italic">
				<IconButton size="small" onClick={() => onCommand('italic')}>
					<FormatItalicIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Underline">
				<IconButton size="small" onClick={() => onCommand('underline')}>
					<FormatUnderlinedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title="Add link">
				<IconButton size="small" onClick={onLink}>
					<LinkIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title="Align left">
				<IconButton size="small" onClick={() => onCommand('justifyLeft')}>
					<FormatAlignLeftIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Align center">
				<IconButton size="small" onClick={() => onCommand('justifyCenter')}>
					<FormatAlignCenterIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Align right">
				<IconButton size="small" onClick={() => onCommand('justifyRight')}>
					<FormatAlignRightIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title="Bullet list">
				<IconButton size="small" onClick={() => onCommand('insertUnorderedList')}>
					<FormatListBulletedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Numbered list">
				<IconButton size="small" onClick={() => onCommand('insertOrderedList')}>
					<FormatListNumberedIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Decrease indent">
				<IconButton size="small" onClick={() => onCommand('outdent')}>
					<FormatIndentDecreaseIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Increase indent">
				<IconButton size="small" onClick={() => onCommand('indent')}>
					<FormatIndentIncreaseIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type HtmlTemplateEditorProps = {
	content: string;
	previewContent?: string;
	placeholders: Record<string, ExpressionModel>;
	onContentChange: (content: string) => void;
	onInsertPlaceholder: (anchor: HTMLElement, insertAt: (token: string) => void) => void;
	onInsertTemplateContentPlaceholder: (insertAt?: (token: string) => void) => void;
	onPlaceholderClick: (id: string, anchor: HTMLElement) => void;
	variableScope: VariableScope | undefined;
	/** When false, the formatting toolbar (bold/italic/lists etc.) is hidden. Defaults to true. */
	showFormatting?: boolean;
	/** ID of the currently active/selected placeholder, used to highlight the matching legend chip. */
	activeId?: string | null;
	/** Current format value for the Text/Html/Markdown toggle. If provided, the toggle is rendered in the toolbar. */
	format?: StringTemplateFormat;
	/** Called when the user switches format via the toggle. */
	onFormatChange?: (format: StringTemplateFormat) => void;
	availablePlaceholderIds?: string[];
	showFormatSelector?: boolean;
	newPlaceholderLabel?: string;
	showNewPlaceholderButton?: boolean;
	showPlaceholderLegend?: boolean;
	showContentSlotButton?: boolean;
	onActivePlaceholderChange?: (id: string | undefined) => void;
};

type RawHtmlViewMode = 'editor' | 'source' | 'preview';

export default function HtmlTemplateEditor({
	content,
	previewContent,
	placeholders,
	onContentChange,
	onInsertPlaceholder,
	onInsertTemplateContentPlaceholder,
	onPlaceholderClick,
	showFormatting = true,
	activeId,
	format,
	onFormatChange,
	availablePlaceholderIds = [],
	showFormatSelector = true,
	newPlaceholderLabel = 'Insert Placeholder',
	showNewPlaceholderButton = true,
	showPlaceholderLegend = true,
	showContentSlotButton = false,
	onActivePlaceholderChange,
}: HtmlTemplateEditorProps) {
	const isTextMode = format !== 'Html';
	const initialRawHtmlMode = format === 'Html' && shouldPreserveRawHtml(content);
	const isTextModeRef = useRef(isTextMode);
	const rawHtmlModeRef = useRef(initialRawHtmlMode);
	const rawHtmlViewModeRef = useRef<RawHtmlViewMode>(initialRawHtmlMode ? 'source' : 'editor');
	const contentRef = useRef(content);
	const rawEditorFrameRef = useRef<HTMLIFrameElement | null>(null);
	const rawEditorSelectionRef = useRef<Range | null>(null);
	const sourceTextareaRef = useRef<HTMLTextAreaElement | null>(null);
	const onActivePlaceholderChangeRef = useRef(onActivePlaceholderChange);
	onActivePlaceholderChangeRef.current = onActivePlaceholderChange;
	const [rawHtmlMode, setRawHtmlMode] = useState(initialRawHtmlMode);
	const [rawHtmlViewMode, setRawHtmlViewMode] = useState<RawHtmlViewMode>(initialRawHtmlMode ? 'source' : 'editor');
	const canPreviewHtml = format === 'Html' && (rawHtmlMode || !!previewContent);
	const showSource = rawHtmlViewMode === 'source';
	const showRawPreview = canPreviewHtml && rawHtmlViewMode === 'preview';
	const showRawEditor = rawHtmlMode && rawHtmlViewMode === 'editor';
	const [sourceHtml, setSourceHtml] = useState(() => (initialRawHtmlMode ? content : ''));
	const [rawEditorHtml, setRawEditorHtml] = useState(() => (initialRawHtmlMode ? content : ''));
	const rawPreviewHtml = useMemo(() => contentToRawHtmlPreview(previewContent ?? content, placeholders), [content, placeholders, previewContent]);

	useEffect(() => {
		isTextModeRef.current = isTextMode;
	});

	useEffect(() => {
		rawHtmlModeRef.current = rawHtmlMode;
	}, [rawHtmlMode]);

	useEffect(() => {
		rawHtmlViewModeRef.current = rawHtmlViewMode;
	}, [rawHtmlViewMode]);

	useEffect(() => {
		contentRef.current = content;
	}, [content]);

	const extensions = useMemo(
		() => [
			StarterKit,
			Underline,
			TextStyle,
			Color,
			Highlight,
			Link.configure({ openOnClick: false }),
			TextAlign.configure({ types: ['heading', 'paragraph'] }),
			PlaceholderExtension,
		],
		[],
	);

	// Ref so the stable editorProps callback can call the latest state setters
	const linkClickHandlerRef = useRef<(href: string, anchor: HTMLElement, openDirect: boolean) => void>(() => {});
	linkClickHandlerRef.current = (href, anchor, openDirect) => {
		if (openDirect) {
			window.open(href, '_blank', 'noopener,noreferrer');
			return;
		}
		setLinkPreview({ anchor, href });
		setLinkEditMode(false);
		setLinkEditUrl(href);
	};

	const editor = useEditor({
		extensions,
		content: contentToHtml(initialRawHtmlMode ? getRawHtmlEditorContent(content) : content, placeholders, isTextMode),
		onUpdate: ({ editor: ed }) => {
			if (rawHtmlModeRef.current) {
				if (rawHtmlViewModeRef.current !== 'editor') {
					return;
				}

				const nextBodyContent = htmlToContent(ed.getHTML(), false);
				onContentChange(updateRawHtmlBodyContent(contentRef.current, nextBodyContent));
				return;
			}

			 onContentChange(htmlToContent(ed.getHTML(), isTextModeRef.current));
		},
		onSelectionUpdate: ({ editor: currentEditor }) => {
			const selection = currentEditor.state.selection;
			const selectedNode = selection instanceof NodeSelection ? selection.node : undefined;
			const adjacentNode = selection.empty ? selection.$from.nodeAfter ?? selection.$from.nodeBefore : undefined;
			const placeholderNode = selectedNode?.type.name === 'expressionPlaceholder'
				? selectedNode
				: adjacentNode?.type.name === 'expressionPlaceholder'
					? adjacentNode
					: undefined;
			onActivePlaceholderChangeRef.current?.(placeholderNode?.attrs.expressionId);
		},
		editorProps: {
			attributes: {
				spellcheck: 'false',
			},
			handleDOMEvents: {
				click: (_view, event) => {
					const linkEl = (event.target as HTMLElement).closest('a') as HTMLElement | null;
					if (linkEl) {
						event.preventDefault();
						event.stopPropagation();
						const href = linkEl.getAttribute('href') ?? '';
						linkClickHandlerRef.current(href, linkEl, event.ctrlKey || event.metaKey);
						return true;
					}
					return false;
				},
			},
		},
	});

	// Sync external content changes into the editor (e.g. when a placeholder is inserted
	// via the toolbar button which updates `content` in the parent).
	const prevContent = useRef(content);
	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		if (content === prevContent.current) return;
		prevContent.current = content;

		if (format === 'Html' && shouldPreserveRawHtml(content) && !rawHtmlMode) {
			rawHtmlModeRef.current = true;
			rawHtmlViewModeRef.current = 'source';
			setRawHtmlMode(true);
			setRawHtmlViewMode('source');
			setSourceHtml(content);
			setRawEditorHtml(content);
			editor.commands.setContent(contentToHtml(getRawHtmlEditorContent(content), placeholders, false), { emitUpdate: false });
			return;
		}

		if (rawHtmlMode) {
			if (showSource) {
				setSourceHtml(content);
				setRawEditorHtml(content);
				return;
			}

			if (!showRawPreview && !showRawEditor) {
				setRawEditorHtml(content);
			}
			return;
		}

		// Only update the editor if the canonical content differs from what's in there now.
		const currentCanonical = htmlToContent(editor.getHTML(), isTextMode);
		if (currentCanonical !== content) {
			editor.commands.setContent(contentToHtml(content, placeholders, isTextMode));
		}
	}, [content, editor, placeholders, isTextMode, rawHtmlMode, showSource, showRawPreview, showRawEditor, format]);

	// When activeId changes (e.g. legend chip clicked), select the matching node in the editor.
	const prevActiveId = useRef<string | null | undefined>(null);
	useEffect(() => {
		if (!editor || editor.isDestroyed || !activeId || activeId === prevActiveId.current) return;
		prevActiveId.current = activeId;
		editor.state.doc.descendants((node, pos) => {
			if (node.type.name === 'expressionPlaceholder' && node.attrs.expressionId === activeId) {
				editor.commands.setNodeSelection(pos);
				return false;
			}
		});
	}, [activeId, editor]);

	/** Insert a placeholder node at the current cursor via Tiptap command. */
	const insertAtCursor = useCallback(
		(token: string) => {
			if (!editor) return;
			const match = token.match(PLACEHOLDER_MATCH_REGEX);
			const id = match?.[1];
			if (!id) return;
			editor
				.chain()
				.focus()
				.insertContent({
					type: 'expressionPlaceholder',
					attrs: { expressionId: id, configured: false },
				})
				.run();
		},
		[editor],
	);

	const handleInsertClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			onInsertPlaceholder(e.currentTarget, insertAtCursor);
		},
		[onInsertPlaceholder, insertAtCursor],
	);

	const handlePlaceholderDrop = useCallback((event: globalThis.DragEvent) => {
		const id = event.dataTransfer?.getData(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE);
		if (!id || !editor) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		const position = editor.view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
		if (position === undefined) {
			insertAtCursor(`{{${id}}}`);
			return;
		}

		editor.chain().focus().insertContentAt(position, { type: 'expressionPlaceholder', attrs: { expressionId: id, configured: false } }).run();
	}, [editor, insertAtCursor]);

	useEffect(() => {
		const dom = editor?.view.dom;
		if (!dom) {
			return;
		}

		const allowPlaceholderDrop = (event: globalThis.DragEvent) => {
			if (event.dataTransfer?.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) {
				event.preventDefault();
			}
		};
		const insertDroppedPlaceholder = (event: globalThis.DragEvent) => {
			if (!event.dataTransfer?.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) {
				return;
			}

			handlePlaceholderDrop(event);
		};

		dom.addEventListener('dragover', allowPlaceholderDrop, true);
		dom.addEventListener('drop', insertDroppedPlaceholder, true);
		return () => {
			dom.removeEventListener('dragover', allowPlaceholderDrop, true);
			dom.removeEventListener('drop', insertDroppedPlaceholder, true);
		};
	}, [editor, handlePlaceholderDrop]);

	const handleSourceDrop = useCallback((event: DragEvent<HTMLTextAreaElement>) => {
		const id = event.dataTransfer.getData(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE);
		if (!id) {
			return;
		}

		event.preventDefault();
		const textarea = event.currentTarget;
		const token = `{{${id}}}`;
		const start = textarea.selectionStart ?? textarea.value.length;
		const end = textarea.selectionEnd ?? start;
		const nextSource = `${textarea.value.slice(0, start)}${token}${textarea.value.slice(end)}`;
		setSourceHtml(nextSource);
		onContentChange(nextSource);
		window.requestAnimationFrame(() => {
			textarea.focus();
			const caret = start + token.length;
			textarea.setSelectionRange(caret, caret);
		});
	}, [onContentChange]);

	const handleSourceSelection = useCallback((event: React.SyntheticEvent<HTMLTextAreaElement>) => {
		const textarea = event.currentTarget;
		onActivePlaceholderChangeRef.current?.(getPlaceholderIdAtPosition(textarea.value, textarea.selectionStart ?? textarea.value.length));
	}, []);

	const insertTemplateContentInSource = useCallback(() => {
		const textarea = sourceTextareaRef.current;
		const selectionStart = textarea?.selectionStart ?? sourceHtml.length;
		const selectionEnd = textarea?.selectionEnd ?? selectionStart;
		const nextSourceHtml = `${sourceHtml.slice(0, selectionStart)}${TEMPLATE_CONTENT_PLACEHOLDER_TOKEN}${sourceHtml.slice(selectionEnd)}`;
		setSourceHtml(nextSourceHtml);
		onContentChange(nextSourceHtml);

		window.requestAnimationFrame(() => {
			textarea?.focus();
			const nextCaret = selectionStart + TEMPLATE_CONTENT_PLACEHOLDER_TOKEN.length;
			textarea?.setSelectionRange(nextCaret, nextCaret);
		});
	}, [onContentChange, sourceHtml]);

	// Link preview popover state
	const [linkPreview, setLinkPreview] = useState<{ anchor: HTMLElement; href: string } | null>(null);
	const [linkEditMode, setLinkEditMode] = useState(false);
	const [linkEditUrl, setLinkEditUrl] = useState('');

	const closeLinkPreview = () => setLinkPreview(null);

	const applyPreviewLink = () => {
		if (!editor) return;
		if (linkEditUrl.trim() === '') {
			editor.chain().focus().unsetLink().run();
		} else {
			editor.chain().focus().setLink({ href: linkEditUrl.trim(), target: '_blank' }).run();
		}
		closeLinkPreview();
	};

	// Handle click on placeholder nodes in the editor (links handled via editorProps.handleDOMEvents)
	useEffect(() => {
		if (!editor) return;
		const handleClick = (event: MouseEvent) => {
			const target = (event.target as HTMLElement).closest('[data-expression-id]') as HTMLElement | null;
			if (target) {
				const id = target.getAttribute('data-expression-id');
				if (id) onPlaceholderClick(id, target);
			}
		};
		const dom = editor.view.dom;
		dom.addEventListener('click', handleClick, { capture: true });
		return () => dom.removeEventListener('click', handleClick, { capture: true });
	}, [editor, onPlaceholderClick]);

	// Reset source mode when switching away from HTML format
	useEffect(() => {
		if (!showFormatting || format !== 'Html') {
			setRawHtmlMode(false);
			setRawHtmlViewMode('editor');
		}
	}, [format, showFormatting]);

	useEffect(() => {
		if (format !== 'Html' || !shouldPreserveRawHtml(content)) {
			return;
		}

		setRawHtmlMode(true);
		setRawHtmlViewMode((current) => (rawHtmlMode ? current : 'source'));
		if (showSource) {
			setSourceHtml(content);
		}
		if (!showRawEditor) {
			setRawEditorHtml(content);
		}
	}, [content, format, rawHtmlMode, showSource, showRawEditor]);

	const toggleSource = () => {
		if (rawHtmlMode) {
			if (showSource) {
				onContentChange(sourceHtml);
				setRawEditorHtml(sourceHtml);
				setRawHtmlViewMode('editor');
			} else {
				setSourceHtml(content);
				setRawHtmlViewMode('source');
			}
			return;
		}

		if (!editor) return;
		if (!showSource) {
			setSourceHtml(content);
			setRawHtmlViewMode('source');
		} else {
			onContentChange(sourceHtml);
			editor.commands.setContent(contentToHtml(sourceHtml, placeholders, isTextMode), { emitUpdate: false });
			setRawHtmlViewMode('editor');
		}
	};

	const toggleRawPreview = () => {
		if (!canPreviewHtml) return;
		if (showSource) {
			onContentChange(sourceHtml);
		}

		setRawHtmlViewMode((current) => (current === 'preview' ? 'editor' : 'preview'));
	};

	const handleFormatSource = useCallback(() => {
		const formatted = formatHtmlSource(sourceHtml);
		setSourceHtml(formatted);
		onContentChange(formatted);
		window.requestAnimationFrame(() => {
			sourceTextareaRef.current?.focus();
		});
	}, [onContentChange, sourceHtml]);

	const handlePasteCapture = useCallback(
		(event: React.ClipboardEvent<HTMLElement>) => {
			if (isTextModeRef.current) {
				return;
			}

			const html = getParsableClipboardHtml(event.clipboardData);
			if (!html) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			const nextContent = html.trim();
			rawHtmlModeRef.current = true;
			rawHtmlViewModeRef.current = 'source';
			setRawHtmlMode(true);
			setSourceHtml(nextContent);
			setRawEditorHtml(nextContent);
			setRawHtmlViewMode('source');
			onContentChange(nextContent);
		},
		[onContentChange],
	);

	const emitRawEditorDocument = useCallback(
		(doc: Document) => {
			const nextContent = serializeHtmlDocument(contentRef.current, doc);
			contentRef.current = nextContent;
			onContentChange(nextContent);
		},
		[onContentChange],
	);

	const saveRawEditorSelection = useCallback(() => {
		const doc = rawEditorFrameRef.current?.contentDocument;
		const selection = doc?.getSelection();
		if (!selection || selection.rangeCount === 0) {
			return;
		}

		rawEditorSelectionRef.current = selection.getRangeAt(0).cloneRange();
	}, []);

	const restoreRawEditorSelection = useCallback(() => {
		const doc = rawEditorFrameRef.current?.contentDocument;
		const range = rawEditorSelectionRef.current;
		if (!doc || !range) {
			return;
		}

		const selection = doc.getSelection();
		selection?.removeAllRanges();
		selection?.addRange(range);
	}, []);

	const executeRawEditorCommand = useCallback(
		(command: string, value?: string) => {
			const doc = rawEditorFrameRef.current?.contentDocument;
			if (!doc) {
				return;
			}

			rawEditorFrameRef.current?.contentWindow?.focus();
			restoreRawEditorSelection();
			doc.execCommand(command, false, value);
			saveRawEditorSelection();
			emitRawEditorDocument(doc);
		},
		[emitRawEditorDocument, restoreRawEditorSelection, saveRawEditorSelection],
	);

	const applyRawEditorLink = useCallback(() => {
		const doc = rawEditorFrameRef.current?.contentDocument;
		if (!doc) {
			return;
		}

		const href = window.prompt('URL');
		if (!href?.trim()) {
			return;
		}

		executeRawEditorCommand('createLink', href.trim());
	}, [executeRawEditorCommand]);

	const handleRawEditorLoad = useCallback(() => {
		const frame = rawEditorFrameRef.current;
		const doc = frame?.contentDocument;
		if (!doc) {
			return;
		}

		doc.designMode = 'on';

		const emitRawEditorContent = () => emitRawEditorDocument(doc);
		const captureSelection = () => saveRawEditorSelection();
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Enter') {
				return;
			}

			if (event.shiftKey) {
				return;
			}

			if (!insertStyledParagraphAfterSelection(doc)) {
				return;
			}

			event.preventDefault();
			emitRawEditorDocument(doc);
		};
		const handlePlaceholderDragOver = (event: globalThis.DragEvent) => {
			if (event.dataTransfer?.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) {
				event.preventDefault();
			}
		};
		const handlePlaceholderDrop = (event: globalThis.DragEvent) => {
			const id = event.dataTransfer?.getData(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE);
			if (!id) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			frame?.contentWindow?.focus();
			doc.execCommand('insertText', false, `{{${id}}}`);
			saveRawEditorSelection();
			emitRawEditorDocument(doc);
		};

		doc.addEventListener('input', emitRawEditorContent);
		doc.addEventListener('blur', emitRawEditorContent, true);
		doc.addEventListener('keydown', handleKeyDown);
		doc.addEventListener('keyup', captureSelection);
		doc.addEventListener('mouseup', captureSelection);
		doc.addEventListener('selectionchange', captureSelection);
		doc.addEventListener('dragover', handlePlaceholderDragOver);
		doc.addEventListener('drop', handlePlaceholderDrop);
	}, [emitRawEditorDocument, saveRawEditorSelection]);

	const insertTemplateContentInRawEditor = useCallback(() => {
		const doc = rawEditorFrameRef.current?.contentDocument;
		if (!doc) {
			return false;
		}

		rawEditorFrameRef.current?.contentWindow?.focus();
		restoreRawEditorSelection();
		doc.execCommand('insertText', false, TEMPLATE_CONTENT_PLACEHOLDER_TOKEN);
		saveRawEditorSelection();
		emitRawEditorDocument(doc);
		return true;
	}, [emitRawEditorDocument, restoreRawEditorSelection, saveRawEditorSelection]);

	const handleInsertTemplateContentClick = useCallback(() => {
		if (showSource) {
			insertTemplateContentInSource();
			return;
		}

		if (showRawEditor && insertTemplateContentInRawEditor()) {
			return;
		}

		onInsertTemplateContentPlaceholder(insertAtCursor);
	}, [insertAtCursor, insertTemplateContentInRawEditor, insertTemplateContentInSource, onInsertTemplateContentPlaceholder, showRawEditor, showSource]);

	const placeholderIds = extractPlaceholderIds(content);

	return (
		<Stack direction="column" sx={{ height: '100%', overflow: 'hidden' }} onDragOver={(event) => {
			if (event.dataTransfer.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) {
				event.preventDefault();
			}
		}} onDrop={(event) => handlePlaceholderDrop(event.nativeEvent)}>
			<Box sx={{ display: 'flex', alignItems: 'center', minHeight: 38, borderBottom: '1px solid', borderColor: 'divider' }}>
				{/* Format toggle */}
				{showFormatSelector && onFormatChange && (
					<>
						<ToggleButtonGroup
							value={format}
							exclusive
							onChange={(_, v: StringTemplateFormat | null) => v && onFormatChange(v)}
							size="small"
							sx={{ mx: 0.5, '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.75rem' } }}
						>
							<ToggleButton value="Text">Text</ToggleButton>
							<ToggleButton value="Html">Html</ToggleButton>
							<ToggleButton value="Markdown">Markdown</ToggleButton>
						</ToggleButtonGroup>
						<Divider orientation="vertical" flexItem />
					</>
				)}
				{/* Insert placeholder - always visible (except source view), right after toggle */}
				{showNewPlaceholderButton && !showSource && !showRawPreview && !showRawEditor && (
					<Button size="small" startIcon={<AddCircleIcon />} variant="text" onClick={handleInsertClick} sx={{ mx: 0.5, whiteSpace: 'nowrap' }}>
						{newPlaceholderLabel}
					</Button>
				)}
				{availablePlaceholderIds.length > 0 && !showSource && !showRawPreview && !showRawEditor && (
					<TemplatePlaceholderMenu placeholderNames={availablePlaceholderIds} onSelect={(id) => insertAtCursor(`{{${id}}}`)} label={TEMPLATE_PLACEHOLDER_MENU_LABEL} />
				)}
				{showContentSlotButton && !showRawPreview && (
					<Button size="small" startIcon={<AddCircleIcon />} variant="text" onClick={handleInsertTemplateContentClick} sx={{ mx: 0.5, whiteSpace: 'nowrap' }}>
						Insert Content Slot
					</Button>
				)}
				{showFormatting && editor && !showSource && !showRawPreview && !showRawEditor && (
					<>
						<Divider orientation="vertical" flexItem />
						<EditorToolbar editor={editor} />
					</>
				)}
				{showFormatting && showRawEditor && (
					<>
						<Divider orientation="vertical" flexItem />
						<RawHtmlEditorToolbar onCommand={executeRawEditorCommand} onLink={applyRawEditorLink} />
					</>
				)}
				{showFormatting && (
					<Tooltip title={showSource ? 'Visual editor' : 'Edit HTML source'}>
						<IconButton size="small" onClick={toggleSource} color={showSource ? 'primary' : 'default'} sx={{ ml: 'auto', mr: 0.5 }}>
							<CodeIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				{showFormatting && showSource && (
					<Tooltip title="Format HTML source">
						<IconButton size="small" onClick={handleFormatSource} sx={{ mr: 0.5 }}>
							<FormatIndentIncreaseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				{showFormatting && canPreviewHtml && (
					<Tooltip title={showRawPreview ? 'Visual editor' : 'Preview HTML'}>
						<IconButton size="small" onClick={toggleRawPreview} color={showRawPreview ? 'primary' : 'default'} sx={{ mr: 0.5 }}>
							<VisibilityIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</Box>
			<Box
				sx={{
					flex: 1,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 1,
					'& .tiptap': {
						minHeight: '100%',
						padding: 1.5,
						outline: 'none',
						'& p': { margin: 0 },
					},
					'& .tiptap.ProseMirror-focused': { borderColor: 'primary.main' },
				}}
			>
				{showSource ? (
					<textarea
						ref={sourceTextareaRef}
						value={sourceHtml}
						onSelect={handleSourceSelection}
						onKeyUp={handleSourceSelection}
						onClick={handleSourceSelection}
						onDragOver={(event) => {
							if (event.dataTransfer.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) event.preventDefault();
						}}
						onDrop={handleSourceDrop}
						onChange={(e) => {
							setSourceHtml(e.target.value);
							onContentChange(e.target.value);
						}}
						spellCheck={false}
						style={{
							flex: 1,
							width: '100%',
							height: '100%',
							resize: 'none',
							border: 'none',
							outline: 'none',
							padding: '12px',
							boxSizing: 'border-box',
							fontFamily: 'monospace',
							fontSize: '0.75rem',
							background: 'transparent',
							color: 'inherit',
						}}
					/>
				) : showRawPreview ? (
					<Box
						component="iframe"
						title="HTML preview"
						srcDoc={rawPreviewHtml}
						sandbox="allow-popups allow-popups-to-escape-sandbox"
						sx={{
							flex: 1,
							width: '100%',
							height: '100%',
							border: 0,
							backgroundColor: '#fff',
						}}
					/>
				) : showRawEditor ? (
					<Box
						component="iframe"
						ref={rawEditorFrameRef}
						title="HTML editor"
						srcDoc={rawEditorHtml}
						sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
						onLoad={handleRawEditorLoad}
						sx={{
							flex: 1,
							width: '100%',
							height: '100%',
							border: 0,
							backgroundColor: '#fff',
						}}
					/>
				) : (
					<Box sx={{ flex: 1, overflow: 'auto' }} onPasteCapture={handlePasteCapture}>
						<EditorContent editor={editor} />
					</Box>
				)}
			</Box>

			{/* Link preview popover */}
			<Popover
				open={Boolean(linkPreview)}
				anchorEl={linkPreview?.anchor}
				onClose={closeLinkPreview}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				disableRestoreFocus
			>
				{linkPreview && (
					<Box sx={{ p: 1, minWidth: 280, maxWidth: 420 }}>
						{linkEditMode ? (
							<Box
								component="form"
								onSubmit={(e) => {
									e.preventDefault();
									applyPreviewLink();
								}}
								sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
							>
								<TextField autoFocus size="small" label="URL" value={linkEditUrl} onChange={(e) => setLinkEditUrl(e.target.value)} sx={{ flex: 1 }} />
								<Button type="submit" variant="contained" size="small">
									Apply
								</Button>
							</Box>
						) : (
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<Typography variant="body2" noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', color: 'primary.main' }}>
									{linkPreview.href}
								</Typography>
								<Tooltip title="Open in new tab (Ctrl+click)">
									<IconButton
										size="small"
										onClick={() => {
											window.open(linkPreview.href, '_blank', 'noopener,noreferrer');
											closeLinkPreview();
										}}
									>
										<OpenInNewIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Edit link">
									<IconButton size="small" onClick={() => setLinkEditMode(true)}>
										<EditIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove link">
									<IconButton
										size="small"
										onClick={() => {
											editor?.chain().focus().unsetLink().run();
											closeLinkPreview();
										}}
									>
										<LinkOffIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Stack>
						)}
					</Box>
				)}
			</Popover>
			{showPlaceholderLegend && placeholderIds.length > 0 && (
				<Box sx={{ pt: 0.5 }}>
					<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
						Placeholders:
					</Typography>
					<Stack direction="row" flexWrap="wrap" gap={0.5}>
						{placeholderIds.map((id) => (
							<Chip
								key={id}
								label={isSystemTemplatePlaceholder(id) ? 'Child content' : id}
								size="small"
								color="primary"
								variant={id === activeId ? 'filled' : 'outlined'}
								title={isSystemTemplatePlaceholder(id) ? 'System placeholder' : 'Template placeholder'}
								onClick={(e) => onPlaceholderClick(id, e.currentTarget)}
								sx={{ cursor: 'pointer' }}
							/>
						))}
					</Stack>
				</Box>
			)}
		</Stack>
	);
}

// Ref is needed for insertAtCursor to be callable from parent via toolbar button
export type { HtmlTemplateEditorProps };
